import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuAlignCenter, LuAlignJustify, LuAlignLeft, LuAlignRight } from 'react-icons/lu';

import type { VisualEditorAlign } from './editor-types';
import { HTML_EDITOR_ALIGNS } from './editor-types';

const ALIGN_ICONS = {
    left: LuAlignLeft,
    center: LuAlignCenter,
    right: LuAlignRight,
    justify: LuAlignJustify
} as const;

export interface AlignMenuProps {
    value: VisualEditorAlign;
    labels: Record<VisualEditorAlign, string> & { menu: string };
    buttonClassName: string;
    onChange(_align: VisualEditorAlign): void;
}

export const AlignMenu = (props: AlignMenuProps) => {
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState<CSSProperties>();
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const TriggerIcon = ALIGN_ICONS[props.value];

    useEffect(() => {
        if (!open) return;

        const updatePosition = () => {
            const rect = triggerRef.current?.getBoundingClientRect();
            if (!rect) return;
            setMenuStyle({
                position: 'fixed',
                top: rect.bottom + 4,
                left: rect.left,
                zIndex: 1050
            });
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const onPointerDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
            setOpen(false);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    return (
        <div className='relative'>
            <button
                id='align-button'
                ref={triggerRef}
                type='button'
                aria-label={props.labels[props.value]}
                title={props.labels[props.value]}
                aria-expanded={open}
                aria-haspopup='menu'
                onClick={() => setOpen((current) => !current)}
                className={props.buttonClassName}>
                <TriggerIcon className='size-3.5' />
            </button>
            {open
                ? createPortal(
                      <div
                          ref={menuRef}
                          role='menu'
                          aria-label={props.labels.menu}
                          style={menuStyle}
                          className='bg-background-surface outline-border-default flex gap-1 rounded-md p-1 outline-1 outline-offset-[-1px]'>
                          {HTML_EDITOR_ALIGNS.map((align) => {
                              const Icon = ALIGN_ICONS[align];
                              return (
                                  <button
                                      key={align}
                                      type='button'
                                      role='menuitem'
                                      aria-label={props.labels[align]}
                                      title={props.labels[align]}
                                      onClick={() => {
                                          props.onChange(align);
                                          setOpen(false);
                                      }}
                                      className={`outline-border-default ${props.buttonClassName}`}>
                                      <Icon className='size-3.5' />
                                  </button>
                              );
                          })}
                      </div>,
                      document.body
                  )
                : null}
        </div>
    );
};
